import CustomLoadingButton from 'Components/CustomLoadingButton'
import DateRangePicker from 'Components/DateRangePicker'
import MultiSelect from 'Components/MultiSelect/MultiSelect'
import StyledForm from 'Components/StyledForm'
import { WithLoader } from 'Components/WithLoader'
import ReportDownload from 'Services/SalesManagement/Features/Report/Components/ReportDownload'
import ReportProgress from 'Services/SalesManagement/Features/Report/Components/ReportProgress'
import { useBaseTicketSaleReport } from 'Services/SalesManagement/Features/Report/Hooks/useBaseTicketSaleReport'
import {
  ReportGenerationStatus,
  type ReportGenerationStatusShape,
  type ReportGenerationTaskShape,
  type ReportResponse,
  type TicketSaleReportParamShape
} from 'Services/SalesManagement/Features/Report/reportSlice'
import type { LoadingStates } from 'Store'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import GenerateReportIcon from '@mui/icons-material/CloudUpload'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import type { FormEvent } from 'react'
import { t } from 'ttag'

interface BaseTicketSaleReportProps {
  reportLoading: LoadingStates
  singleReport: ReportResponse | null
  reportGenerationStatus: ReportGenerationStatusShape | null
  reportGenerationTask: ReportGenerationTaskShape | null
  reportPrefix: string
  reportPath: string
  onCreateReport: (params: TicketSaleReportParamShape) => Promise<unknown>
  onFetchReport: (reportName: string, params: TicketSaleReportParamShape) => Promise<ReportResponse>
  onResetWebsocket: () => void
}

export default function BaseTicketSaleReport({
  reportLoading,
  singleReport,
  reportGenerationStatus,
  reportGenerationTask,
  reportPrefix,
  reportPath,
  onCreateReport,
  onFetchReport,
  onResetWebsocket
}: Readonly<BaseTicketSaleReportProps>) {
  const {
    params,
    setParams,
    authorityOptions,
    ticketSaleTargetOptions,
    isTicketSaleTargetsFetching
  } = useBaseTicketSaleReport()

  const handleReportGenerate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onResetWebsocket()
    const reportParams: TicketSaleReportParamShape = { ...params }
    if (!reportParams.from_date) delete reportParams.from_date
    if (!reportParams.to_date) delete reportParams.to_date
    onCreateReport(reportParams)
      .then(() => {
        SnackbarUtils.success(t`Report generation successfully started.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error starting the report generation.`)
      })
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    if (name !== 'from_date' && name !== 'to_date') return
    const formattedValue = value instanceof Date ? dateFormatter(value).toString() : ''
    setParams(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const handleMultiSelectChange = (selected: string[], name: keyof TicketSaleReportParamShape) => {
    setParams(prevState => ({ ...prevState, [name]: selected }))
  }

  const isLoading = reportLoading === 'pending'
  const isReportGenerationDisabled =
    reportGenerationStatus?.status === ReportGenerationStatus.PENDING ||
    reportGenerationStatus?.status === ReportGenerationStatus.PROGRESS

  return (
    <WithLoader loading={isTicketSaleTargetsFetching}>
      <Paper sx={{ width: '100%', my: 2, p: 2, pt: 1 }}>
        <StyledForm onSubmit={handleReportGenerate} isDisabled={isLoading}>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <MultiSelect
              handleChange={selected => handleMultiSelectChange(selected, 'authorities')}
              label={t`Authority`}
              required={true}
              options={authorityOptions}
            />
          </Box>
          <Box sx={{ minWidth: 200, maxWidth: 400 }}>
            <MultiSelect
              handleChange={selected => handleMultiSelectChange(selected, 'targets')}
              label={t`Target`}
              options={ticketSaleTargetOptions}
            />
          </Box>
          <Box>
            <DateRangePicker
              fromDate={params.from_date}
              toDate={params.to_date}
              onChange={handleDatePickerChange}
            />
          </Box>
          <Box>
            <CustomLoadingButton
              isLoading={isLoading}
              type="submit"
              disabled={isReportGenerationDisabled}
              icon={<GenerateReportIcon />}
              iconPosition="start"
            >{t`Generate report`}</CustomLoadingButton>
          </Box>
          <Box sx={{ my: 4 }}>
            <ReportProgress
              reportGenerationTask={reportGenerationTask}
              reportPrefix={reportPrefix}
              reportStatus={reportGenerationStatus}
              reportPath={reportPath}
              resetReportWebsocketState={onResetWebsocket}
            />
          </Box>
        </StyledForm>
        <ReportDownload
          singleReport={singleReport}
          reportGenerationStatus={reportGenerationStatus}
          fetchReport={reportName => onFetchReport(reportName, params)}
          isLoading={isLoading}
        />
      </Paper>
    </WithLoader>
  )
}
